import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, IconButton, InputBase, useTheme } from '@mui/material'
import { Add, Remove } from '@mui/icons-material'

export type NumberPickerSize = 'small' | 'large'

export type NumberPickerProps = {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  size?: NumberPickerSize
  border?: boolean
}

const widthBySize: Record<NumberPickerSize, number> = {
  small: 46,
  large: 62,
}

const fontSizeBySize: Record<NumberPickerSize, string> = {
  small: '0.875rem',
  large: '1.125rem',
}

const paddingBySize: Record<NumberPickerSize, string> = {
  small: '6px 8px',
  large: '8px 12px',
}

const clampValue = (value: number, min?: number, max?: number) => {
  let next = value
  if (typeof min === 'number') {
    next = Math.max(min, next)
  }
  if (typeof max === 'number') {
    next = Math.min(max, next)
  }
  return next
}

const NumberPicker: React.FC<NumberPickerProps> = ({
  value,
  onChange,
  min = 0,
  max,
  size = 'large',
  border = true,
}) => {
  const theme = useTheme()
  const [inputValue, setInputValue] = useState<string>(String(value))

  useEffect(() => {
    setInputValue(String(value))
  }, [value])

  const cannotDecrease = useMemo(() => value <= min, [min, value])
  const cannotIncrease = useMemo(
    () => (typeof max === 'number' ? value >= max : false),
    [max, value],
  )

  const handleCommit = useCallback(() => {
    const parsed = Number(inputValue.trim())
    if (!Number.isFinite(parsed)) {
      setInputValue(String(value))
      return
    }
    const clamped = clampValue(Math.trunc(parsed), min, max)
    if (clamped !== value) {
      onChange(clamped)
    } else {
      setInputValue(String(clamped))
    }
  }, [inputValue, max, min, onChange, value])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }, [])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        handleCommit()
      }
    },
    [handleCommit],
  )

  const handleBlur = useCallback(() => {
    handleCommit()
  }, [handleCommit])

  const stepValue = useCallback(
    (delta: number) => {
      const next = clampValue(value + delta, min, max)
      if (next !== value) {
        onChange(next)
      }
    },
    [max, min, onChange, value],
  )

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '28px',
        padding: border ? paddingBySize[size] : 0,
        border: border ? `1px solid ${theme.palette.divider}` : 'none',
        width: 'auto',
      }}>
      <IconButton
        size="small"
        onClick={() => stepValue(-1)}
        disabled={cannotDecrease}
        sx={{ p: 0.5 }}>
        <Remove fontSize="small" />
      </IconButton>
      <InputBase
        type="number"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        slotProps={{
          input: {
            min,
            max,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            style: {
              textAlign: 'center',
              width: `${widthBySize[size]}px`,
              fontSize: fontSizeBySize[size],
              fontWeight: size === 'small' ? 500 : 600,
            },
          },
        }}
        sx={{
          px: 1,
          py: 0,
        }}
      />
      <IconButton
        size="small"
        onClick={() => stepValue(1)}
        disabled={cannotIncrease}
        sx={{ p: 0.5 }}>
        <Add fontSize="small" />
      </IconButton>
    </Box>
  )
}

export default NumberPicker
